/* eslint-disable max-lines */
/**
 * Shared types and hit-builder functions for the global search dropdown.
 * Split out of `global-search.tsx` so the main component stays under the
 * project's 300-line file threshold.
 *
 * Server-driven buckets each have a builder that turns the raw API rows
 * into `RenderableHit`s with a resolved route target. The Navigation bucket
 * is different — it's a client-side filter over the shared nav-items list,
 * so its builder takes a query string too.
 */

import {
  BookOpen,
  BookUser,
  Compass,
  FileText,
  Folder as FolderIcon,
  KeyRound,
  Network,
  Search,
  Tag as TagIcon,
  UserCog
} from 'lucide-react';
import { PATHS } from '../../../router/paths';
import {
  ApiKeySearchHit,
  DocumentSearchHit,
  ExportTemplateSearchHit,
  FileSearchHit,
  FolderSearchHit,
  GlossarySearchHit,
  GroupSearchHit,
  SpeakerSearchHit,
  TagSearchHit,
  UserSearchHit
} from '@utils/types/global-search';
import { NavigationItem } from '@utils/hooks/use-navigation-items';

export type BucketKey =
  | 'navigation'
  | 'documents'
  | 'folders'
  | 'users'
  | 'groups'
  | 'glossaries'
  | 'speakers'
  | 'tags'
  | 'export_templates'
  | 'api_keys';

export interface RenderableHit {
  /**
   * Stable per-row key. cmdk uses this as its internal `value` (with fuzzy
   * filtering disabled it just needs to be unique).
   */
  key: string;
  label: string;
  meta?: string;
  href: string;
  icon: typeof Search;
}

export interface Bucket {
  key: BucketKey;
  title: string;
  icon: typeof Search;
  hits: RenderableHit[];
  total: number;
  hasMore: boolean;
  seeAllHref: string;
}

/**
 * Route → which bucket should float to the top of the dropdown. Frozen at
 * panel open time so the list doesn't reshuffle under the cursor while the
 * user is typing.
 */
export function bucketPriorityForRoute(pathname: string): BucketKey | null {
  if (pathname.startsWith('/folders/')) return 'folders';
  if (
    pathname.startsWith('/transcripts/') ||
    pathname.startsWith('/captions/')
  ) {
    return 'documents';
  }
  if (pathname.startsWith('/users')) return 'users';
  if (pathname.startsWith('/teams')) return 'groups';
  if (pathname.startsWith('/glossaries')) return 'glossaries';
  if (pathname.startsWith('/speakers')) return 'speakers';
  if (pathname.startsWith('/tags')) return 'tags';
  if (pathname.startsWith('/export-template-items')) return 'export_templates';
  if (pathname.startsWith('/api-keys')) return 'api_keys';
  return null;
}

// ─── Server-driven buckets ───────────────────────────────────────────────────

export function buildFileHits(items: FileSearchHit[]): RenderableHit[] {
  return items.map(f => {
    const label = f.filename ?? f.session_id;
    return {
      key: `file:${f.session_id}`,
      label,
      meta: f.language?.toUpperCase(),
      icon: FileText,
      // Backend `get_search` on this list expects `field:op:value`.
      href: `${PATHS().SPEECH_TO_TEXT_SESSIONS}?q=${encodeURIComponent(
        `filename:ilike:${label}`
      )}`
    };
  });
}

export function buildDocumentHits(items: DocumentSearchHit[]): RenderableHit[] {
  return items.map(d => {
    const isCaption = d.document_type === 2;
    return {
      key: `doc:${d.document_id}`,
      label: d.filename,
      meta: isCaption ? 'Caption' : 'Transcript',
      icon: FileText,
      href: isCaption
        ? PATHS(d.session_id, d.document_id).CAPTIONS
        : PATHS(d.session_id, d.document_id).TRANSCRIPT
    };
  });
}

export function buildFolderHits(items: FolderSearchHit[]): RenderableHit[] {
  return items.map(fo => ({
    key: `folder:${fo.id}`,
    label: fo.name,
    icon: FolderIcon,
    href: PATHS(String(fo.id)).FOLDER_CONTENT
  }));
}

export function buildUserHits(items: UserSearchHit[]): RenderableHit[] {
  // Deep-link to the user's edit modal via `?edit=<id>`; also carry
  // `?filter=<name>` so the users page can client-filter its loaded rows.
  // We deliberately use `filter` not `q` — `useGetUsers` forwards `?q=` to
  // the server's `get_search` dependency which rejects plain strings.
  return items.map(u => {
    const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ');
    const label = fullName || u.email;
    return {
      key: `user:${u.id}`,
      label,
      meta: fullName ? u.email : undefined,
      icon: UserCog,
      href: `${PATHS().USERS}?edit=${u.id}&filter=${encodeURIComponent(label)}`
    };
  });
}

export function buildGroupHits(items: GroupSearchHit[]): RenderableHit[] {
  return items.map(g => ({
    key: `group:${g.id}`,
    label: g.name,
    icon: Network,
    href: `${PATHS().TEAMS}?edit=${g.id}&filter=${encodeURIComponent(g.name)}`
  }));
}

export function buildGlossaryHits(items: GlossarySearchHit[]): RenderableHit[] {
  return items.map(g => ({
    key: `glossary:${g.id}`,
    label: g.name,
    meta: `${g.source_language.toUpperCase()} → ${g.target_language.toUpperCase()}`,
    icon: BookOpen,
    href: PATHS(String(g.id)).GLOSSARY_DETAILS
  }));
}

// Option-B pattern for these buckets: no per-item edit deep-link, so we
// route to the list page filtered to the exact name. Pages that already
// read `?q=` (Speakers, Tags) filter automatically; pages that don't yet
// (Export templates, API keys) show the unfiltered list — the row is
// still there for the operator to click.

export function buildSpeakerHits(items: SpeakerSearchHit[]): RenderableHit[] {
  return items.map(s => ({
    key: `speaker:${s.id}`,
    label: s.name,
    icon: BookUser,
    href: `${PATHS().SPEAKERS}?q=${encodeURIComponent(`name:ilike:${s.name}`)}`
  }));
}

export function buildTagHits(items: TagSearchHit[]): RenderableHit[] {
  return items.map(tag => ({
    key: `tag:${tag.id}`,
    label: tag.name,
    icon: TagIcon,
    href: `${PATHS().TAGS}?q=${encodeURIComponent(`name:ilike:${tag.name}`)}`
  }));
}

export function buildExportTemplateHits(
  items: ExportTemplateSearchHit[]
): RenderableHit[] {
  // Export templates list doesn't wire `?q=` yet — leaving unfiltered so the
  // list endpoint doesn't 400. Operator lands on the full list.
  return items.map(e => ({
    key: `template:${e.id}`,
    label: e.name,
    icon: FileText,
    href: PATHS().EXPORT_TEMPLATE_ITEMS
  }));
}

export function buildApiKeyHits(items: ApiKeySearchHit[]): RenderableHit[] {
  // Same story as export templates.
  return items.map(k => ({
    key: `apikey:${k.id}`,
    label: k.name,
    icon: KeyRound,
    href: PATHS().API_KEYS
  }));
}

// ─── Navigation bucket (client-side) ────────────────────────────────────────

/**
 * Filter the shared side-nav item list by the query. Match is a
 * case-insensitive substring on the item's label — no fuzzy scoring, no
 * network round-trip, deterministic. Returns at most `limit` items so long
 * lists don't blow out the dropdown.
 */
export function buildNavigationHits(
  navItems: NavigationItem[],
  q: string,
  limit: number
): RenderableHit[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return [];
  const out: RenderableHit[] = [];
  for (const item of navItems) {
    if (item.name.toLowerCase().includes(needle)) {
      out.push({
        key: `nav:${item.href}`,
        label: item.searchLabel ?? item.name,
        icon: (item.icon ?? Compass) as typeof Search,
        href: item.href
      });
      if (out.length >= limit) break;
    }
  }
  return out;
}

/**
 * Total count of navigation items matching q — used to fill the bucket
 * total. Cheap enough to compute alongside `buildNavigationHits`.
 */
export function countNavigationMatches(
  navItems: NavigationItem[],
  q: string
): number {
  const needle = q.trim().toLowerCase();
  if (!needle) return 0;
  let n = 0;
  for (const item of navItems) {
    if (item.name.toLowerCase().includes(needle)) n += 1;
  }
  return n;
}

// ─── Bucket assembly ────────────────────────────────────────────────────────

interface AssembleArgs {
  data: import('@utils/types/global-search').GlobalSearchResponse | undefined;
  navItems: NavigationItem[];
  q: string;
  limit: number;
  /** i18n translation function. Falls back to the second arg if key missing. */
  t: (k: string) => string;
  /**
   * Route-derived priority bucket, frozen at panel-open time so we don't
   * shuffle the list under the user's cursor while they type. The
   * navigation bucket always leads regardless.
   */
  priority: BucketKey | null;
}

/**
 * Turn a `GlobalSearchResponse` + the shared nav-item list into an ordered
 * list of buckets ready to render. Buckets with zero hits are dropped.
 * Ordering: Navigation first, then the route-priority bucket if any, then
 * the rest in canonical order.
 */
/**
 * Format a raw query as a server-side `get_search` directive
 * `field:op:value`. Most customer list endpoints (STT sessions, speakers,
 * tags, glossaries, ...) parse `?q=` through that dependency and reject
 * anything that isn't in `field:op:value` form. Pages that filter
 * client-side (users, teams) don't call the server with `?q=`, so they
 * take the raw string instead.
 */
function serverSearchParam(field: string, q: string): string {
  return q ? `?q=${encodeURIComponent(`${field}:ilike:${q}`)}` : '';
}

export function assembleBuckets({
  data,
  navItems,
  q,
  limit,
  t,
  priority
}: AssembleArgs): Bucket[] {
  const navHits = buildNavigationHits(navItems, q, limit);
  const navTotal = countNavigationMatches(navItems, q);
  const navBucket: Bucket = {
    key: 'navigation',
    title: t('navigation') || 'Navigation',
    icon: Compass,
    hits: navHits,
    total: navTotal,
    hasMore: navTotal > navHits.length,
    // No "see all" landing page for navigation — clicking a hit goes to
    // that page directly. We reuse the workspace home so `hasMore` never
    // shows a broken link even if a bucket somehow overflows.
    seeAllHref: PATHS().HOME
  };

  // Every field is checked defensively. A backend that predates the
  // expanded schema (e.g. production before the deploy) just returns the
  // old subset; missing buckets are silently omitted rather than
  // crashing the render.
  // "See all" URLs carry the current query in whichever format the
  // destination page understands: server-side pages need `field:op:value`
  // (rejected by `get_search` otherwise); Users/Teams filter client-side
  // via a `?filter=` param that doesn't collide with their server's `?q=`.
  const clientFilterQ = q ? `?filter=${encodeURIComponent(q)}` : '';
  const nameQ = serverSearchParam('name', q);
  const serverBuckets: Bucket[] = [];
  if (data?.documents) {
    serverBuckets.push({
      key: 'documents',
      title: t('documents') || 'Documents',
      icon: FileText,
      hits: buildDocumentHits(data.documents.items),
      total: data.documents.total,
      hasMore: data.documents.has_more,
      seeAllHref: PATHS().HOME
    });
  }
  if (data?.folders) {
    serverBuckets.push({
      key: 'folders',
      title: t('folders') || 'Folders',
      icon: FolderIcon,
      hits: buildFolderHits(data.folders.items),
      total: data.folders.total,
      hasMore: data.folders.has_more,
      // Folders-first sort is auto-applied when landing from global search.
      seeAllHref: `${PATHS().HOME}?sortOrder=folders-first`
    });
  }
  if (data?.users) {
    serverBuckets.push({
      key: 'users',
      title: t('users') || 'Users',
      icon: UserCog,
      hits: buildUserHits(data.users.items),
      total: data.users.total,
      hasMore: data.users.has_more,
      seeAllHref: `${PATHS().USERS}${clientFilterQ}`
    });
  }
  if (data?.groups) {
    serverBuckets.push({
      key: 'groups',
      title: t('teams') || 'Teams',
      icon: Network,
      hits: buildGroupHits(data.groups.items),
      total: data.groups.total,
      hasMore: data.groups.has_more,
      seeAllHref: `${PATHS().TEAMS}${clientFilterQ}`
    });
  }
  if (data?.glossaries) {
    serverBuckets.push({
      key: 'glossaries',
      title: t('glossaries') || 'Glossaries',
      icon: BookOpen,
      hits: buildGlossaryHits(data.glossaries.items),
      total: data.glossaries.total,
      hasMore: data.glossaries.has_more,
      seeAllHref: `${PATHS().GLOSSARIES}${nameQ}`
    });
  }
  if (data?.speakers) {
    serverBuckets.push({
      key: 'speakers',
      title: t('speakers') || 'Speakers',
      icon: BookUser,
      hits: buildSpeakerHits(data.speakers.items),
      total: data.speakers.total,
      hasMore: data.speakers.has_more,
      seeAllHref: `${PATHS().SPEAKERS}${nameQ}`
    });
  }
  if (data?.tags) {
    serverBuckets.push({
      key: 'tags',
      title: t('tags') || 'Tags',
      icon: TagIcon,
      hits: buildTagHits(data.tags.items),
      total: data.tags.total,
      hasMore: data.tags.has_more,
      seeAllHref: `${PATHS().TAGS}${nameQ}`
    });
  }
  if (data?.export_templates) {
    serverBuckets.push({
      key: 'export_templates',
      title: t('export-template-items') || 'Export templates',
      icon: FileText,
      hits: buildExportTemplateHits(data.export_templates.items),
      total: data.export_templates.total,
      hasMore: data.export_templates.has_more,
      // Export templates page doesn't wire ?q= yet — leave unfiltered.
      seeAllHref: PATHS().EXPORT_TEMPLATE_ITEMS
    });
  }
  if (data?.api_keys) {
    serverBuckets.push({
      key: 'api_keys',
      title: t('api-keys') || 'API keys',
      icon: KeyRound,
      hits: buildApiKeyHits(data.api_keys.items),
      total: data.api_keys.total,
      hasMore: data.api_keys.has_more,
      // API keys page doesn't wire ?q= yet — leave unfiltered.
      seeAllHref: PATHS().API_KEYS
    });
  }

  const nonEmpty = [navBucket, ...serverBuckets].filter(b => b.hits.length > 0);

  if (priority) {
    // Navigation always leads; if the priority is one of the server buckets,
    // float it to the position just after navigation.
    const navLead = nonEmpty[0]?.key === 'navigation' ? 1 : 0;
    const idx = nonEmpty.findIndex(b => b.key === priority);
    if (idx > navLead) {
      const [head] = nonEmpty.splice(idx, 1);
      nonEmpty.splice(navLead, 0, head);
    }
  }

  return nonEmpty;
}
